import { useState, useCallback, useEffect, useRef } from 'react';
import { PromptConflict, validatePrompt, ValidationResponse } from '../lib/llmService';

export function usePromptValidation(initialPrompt: string = '', delay: number = 1000) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const performValidation = useCallback(async (text: string, signal?: AbortSignal) => {
    if (!text.trim()) {
      setValidation(null);
      return;
    }
    
    setIsValidating(true);
    try {
      const result = await validatePrompt(text, signal);
      if (!signal?.aborted) {
        setValidation(result);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Validation failed:', error);
    } finally {
      if (!signal?.aborted) {
        setIsValidating(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      performValidation(prompt, controller.signal);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      controller.abort();
    };
  }, [prompt, delay, performValidation]);

  const applyFix = useCallback((conflict: PromptConflict) => {
    setPrompt(prev => {
      // Use split/join for global replacement of the specific span to avoid regex issues
      const newPrompt = prev.split(conflict.text_span).join(conflict.suggested_fix);
      return newPrompt;
    });
  }, []);

  const magicEnhance = useCallback(() => {
    if (!validation) return;
    
    if (validation.enhanced_prompt) {
      setPrompt(validation.enhanced_prompt);
    } else if (validation.conflicts && validation.conflicts.length > 0) {
      let enhancedPrompt = prompt;
      // Apply all conflicts in order to avoid index shifting issues
      validation.conflicts.forEach(conflict => {
        enhancedPrompt = enhancedPrompt.split(conflict.text_span).join(conflict.suggested_fix);
      });
      setPrompt(enhancedPrompt);
    }
  }, [prompt, validation]);

  return {
    prompt,
    setPrompt,
    validation,
    isValidating,
    applyFix,
    magicEnhance,
    revalidate: () => performValidation(prompt)
  };
}
